import React, { useEffect, useRef, useState } from 'react';
import { useChatbotContext } from '../context/ChatbotContext'; // Import the context hook
import { useSessionContext } from '../context/SessionContext'; // Import the Session context hook
// Import the custom hook
import usePortalisClientEvent from '../hooks/usePortalisClientEvent';
// Import Font Awesome icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWindowMinimize,
  faWindowMaximize,
  faXmark,
  // faVideo // If you want to replace the Video icon too
} from '@fortawesome/free-solid-svg-icons';
// Import CSS - update path if necessary
import '../styles/chatbot.css';

// URLs from the original code
const avatarPreviewURL = "https://api.mckinley.com/public/videos/AvatarPreview.mp4";
// activeCallURL seems unused in the new design, keeping it commented for now
// const activeCallURL = "https://www.dropbox.com/scl/fi/0kxx4enwsv0hnu0omp2ry/x_image.png?rlkey=77mn0c4bi0ne8hy2le2z5wvfe&st=lvv9mvx7&raw=1";
const avatarImagePreviewURL = "https://api.mckinley.com/public/images/AvatarPreview.png";
// Base URL - parameters will be added dynamically
const baseChatbotURL = "https://chat.portalis.ai/guest?orgId=172f0d3f-04f3-449d-8e39-d399a80b483a&slotId=c701069b-897d-4dc1-8ed9-607a8bfb72c6";
//const baseChatbotURL = "https://chat.portalis.ai/guest?orgId=172f0d3f-04f3-449d-8e39-d399a80b483a&slotId=e278244d-519e-46ac-ad1c-4a1da10db2ef";
// Remove component props interface - no longer needed
// interface ChatbotProps {
//   communitySlug?: string;
// }

console.log('Chatbot component function body executed'); // <-- Add log here

// Remove props from component signature
const Chatbot: React.FC = () => {
  console.log('Chatbot component function body executed'); // <-- Add log here

  const [state, setState] = useState<
    'closed' | 'open' | 'minimized' | 'fullscreen'
  >('closed');
  const videoRef = useRef<HTMLVideoElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isClient, setIsClient] = useState(false);
  const { communitySlug } = useChatbotContext(); // Get current slug from context
  const { sessionId } = useSessionContext(); // Get sessionId from SessionContext
  const [iframeSrc, setIframeSrc] = useState<string | undefined>(undefined); // Start undefined
  const initialUrlSetRef = useRef(false); // Track if initial URL is set

  // Determine if chat is active (not closed)
  const isActive = state !== 'closed';
  // Determine if chat has been opened at least once (not closed, includes minimized)
  const hasStartedChat = state !== 'closed';

  // Mobile redirect handler - minimize the chat
  const handleMobileRedirect = () => {
    if (window.innerWidth < 768) {
      setState('minimized');
    }
  };
  // Use the event listener hook
  usePortalisClientEvent(handleMobileRedirect);

  // Effect to ensure component only renders on the client
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Effect to set the initial iframe URL with the first available slug AND sessionId, only ONCE
  useEffect(() => {
    // Only run on client, if the URL hasn't been set, AND if sessionId is available
    if (isClient && !initialUrlSetRef.current && sessionId) { // Added check for sessionId
      let finalUrl = baseChatbotURL;
      // Append communitySlug if available
      if (communitySlug) {
        finalUrl = `${finalUrl}&communitySlug=${encodeURIComponent(communitySlug)}&sessionId=${encodeURIComponent(sessionId)}`;
      }
      // Append sessionId
      finalUrl = `${finalUrl}&sessionId=${encodeURIComponent(sessionId)}`; // Add sessionId parameter

      // Set the iframe source state with the determined URL
      setIframeSrc(finalUrl);
      // Mark that the initial URL has been set, preventing future updates from this effect
      initialUrlSetRef.current = true;
      console.log('Initial iframe URL set:', finalUrl); // Log initial URL setting
    }
    // This effect depends on isClient, communitySlug, baseChatbotURL, and sessionId.
    // Once initialUrlSetRef.current becomes true, changes won't re-trigger the URL set.
  }, [isClient, communitySlug, baseChatbotURL, sessionId]); // Added sessionId dependency

  // Attempt to play video when component mounts or when state changes back to closed/minimized
  useEffect(() => {
    if (isClient && (state === 'closed' || state === 'minimized') && videoRef.current) {
      const playVideo = async () => {
        try {
          // Resetting the video source can sometimes help ensure it plays
          const sourceElement = videoRef.current?.querySelector('source');
           if (sourceElement) {
             const originalSrc = sourceElement.src;
             sourceElement.src = '';
             sourceElement.src = originalSrc;
             videoRef.current?.load(); // Reload the video element
           }
          await videoRef.current?.play();
        } catch (error) {
          console.log('Auto-play was prevented:', error);
        }
      };
      // Adding a slight delay can sometimes help with autoplay issues
      const timer = setTimeout(playVideo, 100);
      return () => clearTimeout(timer);
    } else if (videoRef.current) {
       videoRef.current.pause(); // Pause video if chat is open/fullscreen
    }
  }, [state, isClient]); // Depend on state and isClient

  // Effect to show/hide the Bablic flags widget iframe
  useEffect(() => {
    if (!isClient) return; // Only run on client

    const bablicIframe = document.querySelector('iframe#bablicFlagsWidget') as HTMLIFrameElement | null;

    if (bablicIframe) {
      if (isActive) {
        // Hide Bablic iframe when chat is active (open, minimized, fullscreen)
        bablicIframe.style.display = 'none';
        console.log('Hiding Bablic iframe');
      } else {
        // Show Bablic iframe when chat is closed
        // Use 'block' or remove the style attribute depending on its default display
        bablicIframe.style.display = 'block'; // Assuming 'block' is the default
        console.log('Showing Bablic iframe');
      }
    } else {
      console.log('Bablic iframe not found');
    }

    // Cleanup function is not strictly necessary here as we are just changing styles,
    // but it's good practice if we were adding/removing listeners etc.

  }, [isActive, isClient]); // Depend on isActive and isClient

  const handleClose = () => {
    // Don't reset iframe src to blank here, keep it persistent
    console.log('Setting state to: closed');
    // if (iframeRef.current) {
    //   iframeRef.current.src = 'about:blank'; // Keep src persistent
    // }
    setState('closed');
  };

  const handleFullScreen = () => {
    console.log(`Setting state to: ${state === 'fullscreen' ? 'open' : 'fullscreen'}`);
    setState(prevState => (prevState === 'fullscreen' ? 'open' : 'fullscreen'));
  };

  const handleOpenChat = () => {
     // Ensure iframe has the correct source when opening
     console.log('Setting state to: open');
     // No need to manually set iframeRef.current.src here anymore,
     // as the src attribute is bound to the iframeSrc state, which is set once initially.
     // if (iframeRef.current && iframeRef.current.src !== iframeSrc && iframeSrc !== 'about:blank') {
     //     iframeRef.current.src = iframeSrc;
     // }
     setState('open');
  };

  // Only render on client
  if (!isClient) return null;

  // Don't render the iframe until its src is determined
  // Check isActive AND that sessionId has loaded AND iframeSrc is set
  const shouldRenderIframe = isActive && sessionId && iframeSrc !== undefined;

  return (
    // Use Bulma 'container' or a simple div for positioning context if needed,
    // but the fixed positioning should handle it. Using 'chat-widget' class from new CSS.
    <div className="chat-widget">
      {/* Chat Modal - Rendered if state is not 'closed' AND iframeSrc is ready */}
      {shouldRenderIframe && (
        <div
          // Combine Bulma modal classes with custom state classes
          className={`chat-modal ${state === 'fullscreen' ? 'is-fullscreen' : ''} ${state === 'minimized' ? 'is-minimized' : ''}`}
          // Prevent clicks inside modal from closing it if overlay click is used
          onClick={(e) => e.stopPropagation()}
        >
          {/* Using Bulma 'box' for styling */}
          <div className="box p-0" style={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#363636' /* Dark grey like Bulma's card-header */ }}>
            {/* Header */}
            <header className="p-3" style={{ backgroundColor: '#363636', color: 'white' /* Bulma card-header-title color */, flexShrink: 0 }}>
              <div className="level is-mobile mb-0"> {/* mb-0 to remove default margin */}
                <div className="level-left">
                  <div className="level-item">
                    <figure className="image is-24x24 mr-2"> {/* Removed is-rounded, let CSS handle */}
                      <img
                        src={avatarImagePreviewURL} // Use constant
                        alt="AI Agent"
                        className="is-rounded" // Add Bulma class for rounded image
                      />
                    </figure>
                    <span className="has-text-weight-medium has-text-white"> {/* Ensure text is white */}
                      Mia the AI Leasing Agent
                    </span>
                  </div>
                </div>
                <div className="level-right">
                  <div className="level-item">
                    {/* Using Bulma 'buttons' container */}
                    <div className="buttons are-small">
                      <button
                        className="button is-ghost has-text-white" // is-ghost for borderless, has-text-white
                        onClick={() => {
                          console.log('Setting state to: minimized');
                          setState('minimized');
                        }}
                        aria-label="Minimize Chat"
                      >
                        <span className="icon">
                           {/* Using FontAwesome icon */}
                          <FontAwesomeIcon icon={faWindowMinimize} />
                        </span>
                      </button>
                      <button
                        className="button is-ghost has-text-white"
                        onClick={handleFullScreen}
                        aria-label={state === 'fullscreen' ? "Exit Fullscreen" : "Enter Fullscreen"}
                      >
                        <span className="icon">
                          {state === 'fullscreen' ? (
                            <FontAwesomeIcon icon={faWindowMaximize} /> // Should be minimize icon when fullscreen? Let's keep maximize for toggle consistency
                          ) : (
                            <FontAwesomeIcon icon={faWindowMaximize} />
                          )}
                        </span>
                      </button>
                      <button
                        className="button is-ghost has-text-white"
                        onClick={handleClose}
                        aria-label="Close Chat"
                      >
                        <span className="icon">
                          <FontAwesomeIcon icon={faXmark} />
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </header>

            {/* Content Area - Takes remaining space */}
            <div className="is-relative" style={{ flexGrow: 1, overflow: 'hidden', backgroundColor: 'white' /* Content area background */ }}>
              {/* Chat iframe Container */}
              {shouldRenderIframe && (
                <div
                  style={{
                    height: '100%', // Fill parent
                    width: '100%',
                  }}
                >
                  <iframe
                    ref={iframeRef}
                    width="100%"
                    height="100%"
                    src={iframeSrc} // iframeSrc now includes sessionId
                    frameBorder="0"
                    allow="camera;microphone;fullscreen;clipboard-write"
                    title="Mia the AI Leasing Agent Chat"
                    style={{ display: 'block' }}
                  />
                </div>
              )}
              {!shouldRenderIframe && isActive && (
                // Optional: Show a loading indicator while waiting for sessionId/iframeSrc
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>Loading Chat...</div>
              )}
            </div> {/* End Content Area */}
          </div> {/* End Box */}
        </div> /* End Chat Modal */
      )}

      {/* Floating Avatar Button - Show if state is 'closed' or 'minimized' */}
      {(state === 'closed' || state === 'minimized') && (
        // Container for button and text hints
        <div className="is-relative floating-button-container" /* Added class for hover targeting */ >
          {/* Pulse Effect */}
          <div
             // Use class for animation, set color via inline style
            className={`pulse-effect`}
            style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                zIndex: -1, // Behind the button
                opacity: 0.5, // Start visible
                // Set background color based on state
                backgroundColor: state === 'minimized' ? 'rgb(14 215 3)' : '#4681c7' /* Custom color for closed state */,
            }}
          />
           <button
             className="floating-button is-relative" // is-relative needed for video content
             // Use Bulma 'button' base class + custom class? No, stick to custom styling.
             onClick={handleOpenChat} // Use specific open handler
             aria-label={state === 'minimized' ? 'Continue Chat' : 'Open Chat'}
             style={{
                 // Apply base styles directly, CSS file handles the rest
                 height: '4rem',
                 width: '4rem',
                 borderRadius: '50%',
                 overflow: 'hidden',
                 border: 'none', // Remove default border
                 padding: 0, // Remove default padding
                 cursor: 'pointer',
                 display: 'flex', // Center video if needed
                 alignItems: 'center',
                 justifyContent: 'center',
                 backgroundColor: state === 'minimized' ? '#48c78e' : '#209cee', // Bulma success/info colors
                 transition: 'all 0.3s ease',
             }}
           >
            <video
              ref={videoRef}
              // className="has-ratio" // Let button size control
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} // Ensure video covers button
              autoPlay
              muted
              loop
              playsInline
              preload="auto" // Changed from metadata to auto
              poster={avatarImagePreviewURL} // Use constant for poster
            >
              <source
                src={avatarPreviewURL} // Use constant
                type="video/mp4"
              />
            </video>
          </button>
            {/* Text Hints - Conditional based on state */}
            {state === 'closed' && (
              <>
                {/* Text Above (Default) */}
                <div
                  className={`above-text`}
                  style={{ backgroundColor: '#2563eb' }} // Apply blue background inline
                >
                  Can I Help?
                </div>
                {/* Text Left (On Hover) */}
                <div
                  className={`hover-text`}
                  style={{ backgroundColor: '#2563eb' }} // Apply blue background inline
                >
                  Talk with our AI Leasing Agent
                </div>
              </>
            )}
            {state === 'minimized' && (
              <>
                {/* Hover Text for Minimized State */}
                <div
                  className={`hover-text`}
                  style={{ backgroundColor: 'rgb(14 215 3)' }} // Apply custom green background inline
                >
                  Continue Chat
                </div>
              </>
            )}
        </div>
      )}
    </div> // End Chat Widget
  );
};

export default Chatbot; 